/**
 * Chat Panel Component
 *
 * Reusable chat panel for split view feature.
 * Contains messages, composer, and all chat functionality.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useCallback, useEffect } from "react";
import type { AIModel } from "@/lib/apiKeyStorage";
import type { UserTier } from "@/hooks/useUserTier";
import MessageList from "@/components/dashboard/MessageList";
import MessageComposer from "@/components/dashboard/MessageComposer";
import RevertUndoButton from "@/components/dashboard/RevertUndoButton";
import RevertWithDraftUndoButton from "@/components/dashboard/RevertWithDraftUndoButton";
import QuickSendButtons from "@/components/splitView/QuickSendButtons";
import { useEncryptedMessages } from "@/hooks/useEncryptedMessages";
import { useMessageComposition } from "@/hooks/useMessageComposition";
import { useMessageActions } from "@/hooks/useMessageActions";
import { useRevertWithDraft } from "@/hooks/useRevertWithDraft";
import { useTextConversion } from "@/hooks/useTextConversion";
import { useStepByStepMode } from "@/hooks/useStepByStepMode";
import type { Thread } from "@/types/chat";
import type { MessageType, QuickSendTarget } from "@/types/splitView";
interface ChatPanelProps {
  threadId: string | null;
  thread: Thread | null;
  userId?: string | undefined; // Deprecated - no longer used, auth is handled internally
  userTier: UserTier;
  selectedModel: AIModel;
  onModelChange: (model: AIModel) => void;
  onThreadTitleUpdated?: () => void;
  onCreateThread: () => Promise<string | null>;
  onForkThread?: (fromThreadId: string, fromMessageId: string, newTitle?: string) => Promise<string | null>;
  isFeatureEnabled?: (feature: string) => boolean;
  showHeader?: boolean;
  headerTitle?: string;
  onAIResponse?: (content: string) => void; // Called when AI responds (for cross-chat)
  crossChatEnabled?: boolean; // Whether cross-chat is active
  exposeSendMessage?: (sendFn: (message: string, files: File[]) => Promise<void>) => void; // Expose sendMessage function
  isMainChat?: boolean; // Is this the main (left) chat panel?
  panelName?: string; // Custom name for this panel
  otherPanelName?: string; // Name of the other panel (for system prompt)
  onPanelNameChange?: (newName: string) => void; // Callback when name is edited
  messageType?: MessageType; // Type of message being sent
  quickSendTargets?: QuickSendTarget[]; // Available targets for quick-send buttons
  onQuickSend?: (targetPanelId: 'left' | 'right', message: string) => void; // Handler for quick-send
  currentPanelId?: 'left' | 'right'; // Current panel ID for quick-send
}
export default function ChatPanel(t0) {
  const $ = _c(139);
  const {
    threadId,
    thread,
    userTier,
    selectedModel,
    onModelChange,
    onThreadTitleUpdated,
    onCreateThread,
    onForkThread,
    isFeatureEnabled: t1,
    showHeader: t2,
    headerTitle,
    onAIResponse,
    crossChatEnabled: t3,
    exposeSendMessage,
    isMainChat: t4,
    panelName: t5,
    otherPanelName: t6,
    onPanelNameChange,
    messageType: t7,
    quickSendTargets: t8,
    onQuickSend,
    currentPanelId: t9
  } = t0;
  const isFeatureEnabled = t1 === undefined ? _temp : t1;
  const showHeader = t2 === undefined ? true : t2;
  const crossChatEnabled = t3 === undefined ? false : t3;
  const isMainChat = t4 === undefined ? false : t4;
  const panelName = t5 === undefined ? "Chat" : t5;
  const otherPanelName = t6 === undefined ? "Other Chat" : t6;
  const messageType = t7 === undefined ? "instruction" : t7;
  let t10;
  if ($[0] !== t8) {
    t10 = t8 === undefined ? [] : t8;
    $[0] = t8;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const quickSendTargets = t10;
  const currentPanelId = t9 === undefined ? "left" : t9;
  const messagesContainerRef = useRef(null);
  const {
    isStepByStepWithExplanation,
    isStepByStepNoExplanation,
    toggleStepByStepWithExplanation,
    toggleStepByStepNoExplanation,
    getSystemPromptAddition
  } = useStepByStepMode();
  const [enableWebSearch, setEnableWebSearch] = React.useState(true);
  let t11;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setEnableWebSearch(_temp2);
    };
    $[2] = t11;
  } else {
    t11 = $[2];
  }
  const toggleWebSearch = t11;
  let t12;
  if ($[3] !== crossChatEnabled || $[4] !== getSystemPromptAddition || $[5] !== isMainChat || $[6] !== messageType || $[7] !== otherPanelName || $[8] !== panelName) {
    t12 = () => {
      let prompt = getSystemPromptAddition();
      if (crossChatEnabled && isMainChat) {
        if (messageType === "instruction") {
          const workflowInstruction = `\n\nWORKFLOW MODE (INSTRUCTION): You are "${panelName}" in a split-view interface. When the user asks you to send a message or instruction to "${otherPanelName}", your ENTIRE response will be automatically sent to them. DO NOT format it as a message or add any meta-commentary. Just write EXACTLY what you want ${otherPanelName} to receive. For example, if the user says "send a message to ${otherPanelName}: hello world", you should respond ONLY with "hello world" - nothing else. Your response IS the message that will be sent. ${otherPanelName} is in INSTRUCTION MODE and will execute without responding unless they need clarification.`;
          prompt = prompt + workflowInstruction;
        } else {
          const workflowInstruction_0 = `\n\nWORKFLOW MODE (CHAT): You are "${panelName}" in a split-view interface. When the user asks you to send a message to "${otherPanelName}", your ENTIRE response will be automatically sent to them. DO NOT format it as a message or add any meta-commentary. Just write EXACTLY what you want ${otherPanelName} to receive. Your response IS the message. ${otherPanelName} is in CHAT MODE and will respond conversationally once, then wait for user feedback.`;
          prompt = prompt + workflowInstruction_0;
        }
      } else {
        if (crossChatEnabled && !isMainChat) {
          if (messageType === "instruction") {
            const workflowInstruction_1 = `\n\nWORKFLOW MODE (INSTRUCTION): You are "${panelName}" - a fully capable AI assistant with all standard capabilities including web search, code execution, and any tools you normally have access to. You may receive instructions from "${otherPanelName}" (visible on the left). These will be marked with "[Instruction from ${otherPanelName}]". The user will refer to you as "${panelName}" and the other AI as "${otherPanelName}". Work on these instructions independently using ALL your available capabilities. DO NOT respond back to ${otherPanelName} unless you genuinely need clarifying questions for accuracy. Execute the task, show your work to the user, and use any tools/capabilities you need to complete the task.`;
            prompt = prompt + workflowInstruction_1;
          } else {
            const workflowInstruction_2 = `\n\nWORKFLOW MODE (CHAT): You are "${panelName}" - a fully capable AI assistant with all standard capabilities. You may receive messages from "${otherPanelName}" (visible on the left). These will be marked with "[Chat from ${otherPanelName}]". The user will refer to you as "${panelName}" and the other AI as "${otherPanelName}". Respond conversationally to ${otherPanelName} using all your available capabilities. After your FIRST response, STOP and wait for the user to provide feedback or guidance before continuing the conversation.`;
            prompt = prompt + workflowInstruction_2;
          }
        }
      }
      return prompt;
    };
    $[3] = crossChatEnabled;
    $[4] = getSystemPromptAddition;
    $[5] = isMainChat;
    $[6] = messageType;
    $[7] = otherPanelName;
    $[8] = panelName;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const buildSystemPrompt = t12;
  const t13 = buildSystemPrompt();
  let t14;
  if ($[10] !== enableWebSearch || $[11] !== onThreadTitleUpdated || $[12] !== t13 || $[13] !== userTier) {
    t14 = {
      onThreadTitleUpdated,
      systemPromptAddition: t13,
      userTier,
      enableWebSearch
    };
    $[10] = enableWebSearch;
    $[11] = onThreadTitleUpdated;
    $[12] = t13;
    $[13] = userTier;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const {
    messages,
    loadingMessages,
    messagesError,
    encryptionError,
    sendInFlight,
    summarizeInFlight,
    sendMessage,
    refreshMessages
  } = useEncryptedMessages(threadId, t14);
  let t15;
  if ($[15] !== onCreateThread || $[16] !== sendMessage || $[17] !== threadId) {
    t15 = {
      selectedThreadId: threadId,
      sendMessage,
      createThread: onCreateThread
    };
    $[15] = onCreateThread;
    $[16] = sendMessage;
    $[17] = threadId;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const {
    draft,
    setDraft,
    attachedFiles,
    setAttachedFiles,
    handleSend
  } = useMessageComposition(t15);
  let t16;
  if ($[19] !== setDraft) {
    t16 = convertedText => setDraft(convertedText);
    $[19] = setDraft;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t16 || $[22] !== userTier) {
    t17 = {
      onTextConverted: t16,
      userTier
    };
    $[21] = t16;
    $[22] = userTier;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const {
    convertingToMarkdown,
    convertingToJson,
    convertToMarkdown,
    convertToJson
  } = useTextConversion(t17);
  let t18;
  if ($[24] !== convertToMarkdown || $[25] !== draft) {
    t18 = () => convertToMarkdown(draft);
    $[24] = convertToMarkdown;
    $[25] = draft;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleConvertToMarkdown = t18;
  let t19;
  if ($[27] !== convertToJson || $[28] !== draft) {
    t19 = () => convertToJson(draft);
    $[27] = convertToJson;
    $[28] = draft;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const handleConvertToJson = t19;
  const t20 = thread?.title ?? null;
  const t21 = onForkThread as any || _temp3;
  const t22 = onThreadTitleUpdated as any || _temp4;
  let t23;
  if ($[30] !== messages || $[31] !== onModelChange || $[32] !== refreshMessages || $[33] !== selectedModel || $[34] !== t20 || $[35] !== t21 || $[36] !== t22 || $[37] !== threadId) {
    t23 = {
      threadId,
      currentThreadTitle: t20,
      messages,
      refreshMessages,
      forkThread: t21,
      refreshThreads: t22,
      onModelChange,
      currentModel: selectedModel
    };
    $[30] = messages;
    $[31] = onModelChange;
    $[32] = refreshMessages;
    $[33] = selectedModel;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
    $[37] = threadId;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const {
    revertInFlight,
    forkFromMessageInFlight,
    handleRevertToMessage,
    handleForkFromMessage,
    canUndoRevert,
    undoRevertInFlight,
    handleUndoRevert
  } = useMessageActions(t23);
  let t24;
  if ($[39] !== draft || $[40] !== messages || $[41] !== onModelChange || $[42] !== refreshMessages || $[43] !== selectedModel || $[44] !== setAttachedFiles || $[45] !== setDraft || $[46] !== threadId) {
    t24 = {
      threadId,
      messages,
      refreshMessages,
      onModelChange,
      onDraftChange: setDraft,
      onAttachmentsChange: setAttachedFiles,
      currentModel: selectedModel,
      currentDraft: draft
    };
    $[39] = draft;
    $[40] = messages;
    $[41] = onModelChange;
    $[42] = refreshMessages;
    $[43] = selectedModel;
    $[44] = setAttachedFiles;
    $[45] = setDraft;
    $[46] = threadId;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const {
    revertWithDraftInFlight,
    handleRevertWithDraft: handleRevertWithDraftCore,
    canUndoRevertWithDraft,
    undoRevertWithDraftInFlight,
    handleUndoRevertWithDraft
  } = useRevertWithDraft(t24);
  const isMessageOperationInProgress = revertInFlight || forkFromMessageInFlight || revertWithDraftInFlight;
  let t25;
  if ($[48] !== handleRevertToMessage || $[49] !== isMessageOperationInProgress) {
    t25 = async (messageId, switchToOriginalModel) => {
      if (isMessageOperationInProgress) {
        return;
      }
      await handleRevertToMessage(messageId, switchToOriginalModel);
    };
    $[48] = handleRevertToMessage;
    $[49] = isMessageOperationInProgress;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const handleRevertToMessageGuarded = t25;
  let t26;
  if ($[51] !== handleRevertWithDraftCore || $[52] !== isMessageOperationInProgress) {
    t26 = async (messageId_0, switchToOriginalModel_0) => {
      if (isMessageOperationInProgress) {
        return;
      }
      await handleRevertWithDraftCore(messageId_0, switchToOriginalModel_0);
    };
    $[51] = handleRevertWithDraftCore;
    $[52] = isMessageOperationInProgress;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const handleRevertWithDraftGuarded = t26;
  let t27;
  if ($[54] !== handleForkFromMessage || $[55] !== isMessageOperationInProgress) {
    t27 = async messageId_1 => {
      if (isMessageOperationInProgress) {
        return;
      }
      await handleForkFromMessage(messageId_1);
    };
    $[54] = handleForkFromMessage;
    $[55] = isMessageOperationInProgress;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  const handleForkFromMessageGuarded = t27;
  const lastMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const lastCrossChatStateRef = useRef(crossChatEnabled);
  let t28;
  let t29;
  if ($[57] !== crossChatEnabled || $[58] !== messages || $[59] !== onAIResponse) {
    t28 = () => {
      if (isInitialLoadRef.current && messages.length > 0) {
        lastMessageCountRef.current = messages.length;
        isInitialLoadRef.current = false;
        return;
      }
      if (crossChatEnabled && !lastCrossChatStateRef.current) {
        lastMessageCountRef.current = messages.length;
        lastCrossChatStateRef.current = crossChatEnabled;
        return;
      }
      lastCrossChatStateRef.current = crossChatEnabled;
      if (!crossChatEnabled || !onAIResponse) {
        return;
      }
      if (messages.length > lastMessageCountRef.current) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          onAIResponse(lastMessage.content);
        }
      }
      lastMessageCountRef.current = messages.length;
    };
    t29 = [messages, crossChatEnabled, onAIResponse];
    $[57] = crossChatEnabled;
    $[58] = messages;
    $[59] = onAIResponse;
    $[60] = t28;
    $[61] = t29;
  } else {
    t28 = $[60];
    t29 = $[61];
  }
  useEffect(t28, t29);
  let t30;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => {
      isInitialLoadRef.current = true;
      lastMessageCountRef.current = 0;
    };
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== threadId) {
    t31 = [threadId];
    $[63] = threadId;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  useEffect(t30, t31);
  let t32;
  let t33;
  if ($[65] !== exposeSendMessage || $[66] !== sendMessage) {
    t32 = () => {
      if (exposeSendMessage && sendMessage) {
        const wrappedSendMessage = async (message, files) => {
          await sendMessage(message, files);
        };
        exposeSendMessage(wrappedSendMessage);
      }
    };
    t33 = [exposeSendMessage, sendMessage];
    $[65] = exposeSendMessage;
    $[66] = sendMessage;
    $[67] = t32;
    $[68] = t33;
  } else {
    t32 = $[67];
    t33 = $[68];
  }
  useEffect(t32, t33);
  let t34;
  if ($[69] !== headerTitle || $[70] !== showHeader || $[71] !== thread?.title) {
    t34 = showHeader && <div className="flex-shrink-0 border-b border-white/30 px-4 py-3"><h2 className="text-lg font-semibold text-foreground">{headerTitle || thread?.title || "New Chat"}</h2></div>;
    $[69] = headerTitle;
    $[70] = showHeader;
    $[71] = thread?.title;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span className="text-xs font-medium text-foreground/60">Panel Name:</span>;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== onPanelNameChange) {
    t36 = e => onPanelNameChange?.(e.target.value);
    $[74] = onPanelNameChange;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== panelName || $[77] !== t36) {
    t37 = <div className="flex-shrink-0 border-b border-white/20 bg-white/20 backdrop-blur-sm px-4 py-2"><div className="flex items-center gap-2">{t35}<input type="text" value={panelName} onChange={t36} className="flex-1 rounded-lg border border-white/40 bg-white/60 px-2 py-1 text-sm text-foreground placeholder-foreground/40 focus:border-sky focus:outline-none focus:ring-1 focus:ring-sky" placeholder="Enter panel name..." /></div></div>;
    $[76] = panelName;
    $[77] = t36;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== encryptionError || $[80] !== messagesError) {
    t38 = (messagesError || encryptionError) && <div className="flex-shrink-0 mx-4 mt-3 rounded-lg border border-red-300 bg-red-100/80 backdrop-blur-sm px-4 py-2 text-sm text-red-700">{messagesError || encryptionError}</div>;
    $[79] = encryptionError;
    $[80] = messagesError;
    $[81] = t38;
  } else {
    t38 = $[81];
  }
  const t39 = sendInFlight || summarizeInFlight;
  let t40;
  if ($[82] !== handleForkFromMessageGuarded || $[83] !== handleRevertToMessageGuarded || $[84] !== handleRevertWithDraftGuarded || $[85] !== isFeatureEnabled || $[86] !== isMessageOperationInProgress || $[87] !== loadingMessages || $[88] !== messages || $[89] !== selectedModel || $[90] !== t39) {
    t40 = <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-4"><MessageList messages={messages} loading={loadingMessages} thinking={t39} currentModel={selectedModel} onRevertToMessage={handleRevertToMessageGuarded} onRevertWithDraft={handleRevertWithDraftGuarded} onForkFromMessage={handleForkFromMessageGuarded} messageActionsDisabled={isMessageOperationInProgress} isFeatureEnabled={isFeatureEnabled} /></div>;
    $[82] = handleForkFromMessageGuarded;
    $[83] = handleRevertToMessageGuarded;
    $[84] = handleRevertWithDraftGuarded;
    $[85] = isFeatureEnabled;
    $[86] = isMessageOperationInProgress;
    $[87] = loadingMessages;
    $[88] = messages;
    $[89] = selectedModel;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== canUndoRevert || $[93] !== handleUndoRevert || $[94] !== undoRevertInFlight) {
    t41 = <RevertUndoButton canUndo={canUndoRevert} undoInFlight={undoRevertInFlight} onUndo={handleUndoRevert} />;
    $[92] = canUndoRevert;
    $[93] = handleUndoRevert;
    $[94] = undoRevertInFlight;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[96] !== canUndoRevertWithDraft || $[97] !== handleUndoRevertWithDraft || $[98] !== undoRevertWithDraftInFlight) {
    t42 = <RevertWithDraftUndoButton canUndo={canUndoRevertWithDraft} undoInFlight={undoRevertWithDraftInFlight} onUndo={handleUndoRevertWithDraft} />;
    $[96] = canUndoRevertWithDraft;
    $[97] = handleUndoRevertWithDraft;
    $[98] = undoRevertWithDraftInFlight;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== t41 || $[101] !== t42) {
    t43 = <div className="border-t border-white/20">{t41}{t42}</div>;
    $[100] = t41;
    $[101] = t42;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== currentPanelId || $[104] !== draft || $[105] !== onQuickSend || $[106] !== quickSendTargets || $[107] !== sendInFlight) {
    t44 = quickSendTargets.length > 0 && onQuickSend && <QuickSendButtons targets={quickSendTargets} currentDraft={draft} onQuickSend={onQuickSend} disabled={sendInFlight} currentPanelId={currentPanelId} />;
    $[103] = currentPanelId;
    $[104] = draft;
    $[105] = onQuickSend;
    $[106] = quickSendTargets;
    $[107] = sendInFlight;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== attachedFiles || $[110] !== convertingToJson || $[111] !== convertingToMarkdown || $[112] !== draft || $[113] !== enableWebSearch || $[114] !== handleConvertToJson || $[115] !== handleConvertToMarkdown || $[116] !== handleSend || $[117] !== isFeatureEnabled || $[118] !== isStepByStepNoExplanation || $[119] !== isStepByStepWithExplanation || $[120] !== onModelChange || $[121] !== selectedModel || $[122] !== sendInFlight || $[123] !== setAttachedFiles || $[124] !== setDraft || $[125] !== toggleStepByStepNoExplanation || $[126] !== toggleStepByStepWithExplanation || $[127] !== userTier) {
    t45 = <div className="border-t border-white/30 px-4 py-3"><MessageComposer value={draft} onChange={setDraft} onSend={handleSend} disabled={sendInFlight} selectedModel={selectedModel} onModelChange={onModelChange} onSummarize={_temp5} onSummarizeAndContinue={_temp6} onFork={_temp7} canSummarize={false} summarizing={false} summarizingAndContinuing={false} forking={false} attachedFiles={attachedFiles} onFilesChange={setAttachedFiles} onConvertToMarkdown={handleConvertToMarkdown} onConvertToJson={handleConvertToJson} convertingToMarkdown={convertingToMarkdown} convertingToJson={convertingToJson} isStepByStepWithExplanation={isStepByStepWithExplanation} isStepByStepNoExplanation={isStepByStepNoExplanation} onToggleStepByStepWithExplanation={toggleStepByStepWithExplanation} onToggleStepByStepNoExplanation={toggleStepByStepNoExplanation} enableWebSearch={enableWebSearch} onToggleWebSearch={toggleWebSearch} userTier={userTier} isFeatureEnabled={isFeatureEnabled} /></div>;
    $[109] = attachedFiles;
    $[110] = convertingToJson;
    $[111] = convertingToMarkdown;
    $[112] = draft;
    $[113] = enableWebSearch;
    $[114] = handleConvertToJson;
    $[115] = handleConvertToMarkdown;
    $[116] = handleSend;
    $[117] = isFeatureEnabled;
    $[118] = isStepByStepNoExplanation;
    $[119] = isStepByStepWithExplanation;
    $[120] = onModelChange;
    $[121] = selectedModel;
    $[122] = sendInFlight;
    $[123] = setAttachedFiles;
    $[124] = setDraft;
    $[125] = toggleStepByStepNoExplanation;
    $[126] = toggleStepByStepWithExplanation;
    $[127] = userTier;
    $[128] = t45;
  } else {
    t45 = $[128];
  }
  let t46;
  if ($[129] !== t40 || $[130] !== t43 || $[131] !== t44 || $[132] !== t45) {
    t46 = <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/40 bg-white/40 backdrop-blur-md shadow-lg m-3">{t40}{t43}{t44}{t45}</div>;
    $[129] = t40;
    $[130] = t43;
    $[131] = t44;
    $[132] = t45;
    $[133] = t46;
  } else {
    t46 = $[133];
  }
  let t47;
  if ($[134] !== t34 || $[135] !== t37 || $[136] !== t38 || $[137] !== t46) {
    t47 = <div className="flex h-full flex-col overflow-hidden">{t34}{t37}{t38}{t46}</div>;
    $[134] = t34;
    $[135] = t37;
    $[136] = t38;
    $[137] = t46;
    $[138] = t47;
  } else {
    t47 = $[138];
  }
  return t47;
}
function _temp7() {}
function _temp6() {}
function _temp5() {}
async function _temp4() {}
async function _temp3() {
  return null;
}
function _temp2(prev) {
  return !prev;
}
function _temp() {
  return true;
}